import React, { useState, useContext, useMemo } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';
import Translate from '@prisjakt/ui-translate';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import { StarIcon } from '@prisjakt/ui-icons';
import RatingHelper from './rating-helper';

import {
  STAR_ICON_SIZE_SMALL,
  STAR_ICON_SIZE_MEDIUM,
  STAR_ICON_SIZE_LARGE,
} from './constants';

import * as S from './styles';

import {
  Size,
  Rate,
  Count,
  OnChange,
  Hide,
  Interactive,
  ShowRate,
  SingleStar,
  CenterAlign,
  CounterPosition,
  ShowRateAs,
  NrOfDecimals,
  ShowCounter,
  ShowCounterAs,
  SecondaryColors,
  CountTranslation,
} from './types';

type Props = {
  className?: string;
  size?: Size;
  rate?: Rate;
  onChange?: OnChange;
  interactive?: Interactive;
  hide?: Hide;
  count?: Count;
  showRate?: ShowRate;
  singleStar?: SingleStar;
  centerAlign?: CenterAlign;
  counterPosition?: CounterPosition;
  showRateAs?: ShowRateAs;
  nrOfDecimals?: NrOfDecimals;
  showCounter?: ShowCounter;
  showCounterAs?: ShowCounterAs;
  secondaryColors?: SecondaryColors;
  countTranslation?: CountTranslation;
};

const Rating = ({
  className,
  interactive = false,
  size = 'medium',
  hide = false,
  count = 0,
  rate = 0,
  nrOfDecimals = 1,
  onChange,
  singleStar = false,
  centerAlign = false,
  showCounter = true,
  showCounterAs = 'text',
  showRate = false,
  showRateAs = 'number',
  counterPosition = 'right',
  secondaryColors = false,
  countTranslation = {
    domain: 'reviews',
    key: 'rating.n-reviews',
  },
}: Props) => {
  const { t } = useContext(AppContext);

  if (hide) return null;

  const starCount = singleStar ? 1 : 5;
  const stars = [...new Array(starCount)].map((_, i) => i + 1);
  const rating = RatingHelper.convert(rate ?? 0, nrOfDecimals);
  const halfStar = rating.half !== rating.whole;

  let starSize;
  switch (size) {
    case 'large':
      starSize = STAR_ICON_SIZE_LARGE;
      break;
    case 'medium':
      starSize = STAR_ICON_SIZE_MEDIUM;
      break;
    case 'small':
      starSize = STAR_ICON_SIZE_SMALL;
      break;
    default:
      break;
  }

  if (interactive)
    return (
      <InteractiveRating
        stars={stars}
        size={size}
        onChange={onChange}
        rate={rate}
      />
    );

  return (
    <S.Rating
      className={className}
      centerAlign={centerAlign}
      aria-label={t('reviews', 'rating.rate-n-text-version', {
        n: rating.rounded,
      })}
    >
      <S.StarsWrapper counterPosition={counterPosition}>
        <S.StarIcons
          secondaryColors={secondaryColors}
          halfStar={halfStar}
          stars={stars}
          rating={rating}
          size={starSize}
        />

        {showRate && rating.whole > 0 && (
          <>
            {showRateAs === 'text' ? (
              <S.RateText size={size}>
                <Translate domain="reviews" n={rating.rounded}>
                  rating.rate-n-text-version.md
                </Translate>
              </S.RateText>
            ) : (
              <S.RateNumber size={size}>{rating.rounded}</S.RateNumber>
            )}
          </>
        )}
      </S.StarsWrapper>

      {showCounter && count !== null && count > 0 && (
        <S.Counter
          interactive={!!onChange}
          size={size}
          counterPosition={counterPosition}
        >
          {showCounterAs === 'text'
            ? `(${t(
                countTranslation.domain,
                countTranslation.key as keyof ParamsAndReturnTypes[CountTranslation['domain']],
                {
                  n: count,
                }
              )})`
            : `(${count})`}
        </S.Counter>
      )}
    </S.Rating>
  );
};

type InteractiveRatingProps = {
  stars: number[];
  size?: Size;
  rate?: Rate;
  onChange?: OnChange;
};

const InteractiveRating = ({
  stars,
  size,
  rate,
  onChange,
}: InteractiveRatingProps) => {
  const [selectedRating, setSelectedRating] = useState(0);

  const handleChange = (rating: number, e: React.ChangeEvent) => {
    if (onChange) {
      onChange(rating * 2, e);
    }
    setSelectedRating(rating);
  };

  const reverseStars = useMemo(() => {
    return stars.reverse();
  }, [stars]);

  // Uses a css technique to fills every star on the left side of selected radio button, in this labels
  // are floated to the right. To emit correct values i.e. first star emits 1 and so forth,
  // the map of rate values is reversed.
  return (
    <S.Rating>
      <S.Fieldset>
        {reverseStars.map(current => (
          <React.Fragment key={current}>
            <input
              name="rating"
              id={`rate-input-${current}`}
              checked={
                selectedRating > 0
                  ? current === selectedRating
                  : current === RatingHelper.toWhole(rate ?? 0)
              }
              onChange={e => handleChange(current, e)}
              value={current * 2} // back to range of 1..10
              type="radio"
            />

            {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
            <label htmlFor={`rate-input-${current}`}>
              <StarIcon height={size === 'medium' ? 16 : 12} />
            </label>
          </React.Fragment>
        ))}
      </S.Fieldset>
    </S.Rating>
  );
};

export default Rating;
